/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators, AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { Logger } from '@app/core';
import { Contact } from '@app/shared/components/contact/contact.model';
import { ContactTypes } from '@app/shared/components/contact/contact-types.enum';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

const log = new Logger('ContactComponent');

@Component({
  selector: 'app-contact',
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss'],
})
export class ContactComponent implements OnInit {
  @Input() parentFormGroup: FormGroup;
  @Input() cols = 12;
  @Input() required = false;
  @Input() showNote = false;
  colsCalculated = 4;
  constructor(private http: HttpClient) {}

  public static phoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) {
        return null;
      }
      const typeId = control.parent.get('type_id')?.value;
      const isPhoneType = [
        ContactTypes.PHONE,
        ContactTypes.MOBILE,
        ContactTypes.MOBILE_PHONE,
        ContactTypes.FAX,
      ].includes(typeId);

      if (isPhoneType && control.value) {
        const valid = /^[0-9]+$/.test(control.value);
        return valid ? null : { invalidPhone: true };
      }
      return null;
    };
  }

  public static getEmptyContact(required: boolean = false): FormGroup {
    const requiredRules: ValidatorFn[] = required ? [Validators.required] : [];

    const typeIdControl = new FormControl(null, requiredRules);
    const valueControl = new FormControl('', [...requiredRules, ContactComponent.phoneValidator()]);

    const formGroup = new FormGroup({
      id: new FormControl(null),
      type_id: typeIdControl,
      value: valueControl,
      // `duplicate` is a non-blocking flag used to indicate duplicates to the UI
      duplicate: new FormControl(false),
      // `duplicateMessage` holds server-provided message for the duplicate (if any)
      duplicateMessage: new FormControl(null),
      // `duplicateChecked` indicates whether this contact was checked already
      duplicateChecked: new FormControl(false),
      note: new FormControl('', [Validators.maxLength(255)]),
    });

    const resetDuplicateCheck = () => {
      formGroup.get('duplicateChecked')?.setValue(false, { emitEvent: false });
      formGroup.get('duplicate')?.setValue(false, { emitEvent: false });
      formGroup.get('duplicateMessage')?.setValue(null, { emitEvent: false });
    };

    typeIdControl.valueChanges.subscribe(() => {
      valueControl.updateValueAndValidity({ emitEvent: false });
      resetDuplicateCheck();
    });

    valueControl.valueChanges.subscribe(() => {
      resetDuplicateCheck();
    });

    return formGroup;
  }

  formatPhoneNumber(index: number) {
    const contactsArray = this.parentFormGroup.get('contacts') as FormArray;
    if (!contactsArray) return;
    const contactGroup = contactsArray.at(index) as FormGroup;
    if (!contactGroup) return;

    const typeId = contactGroup.get('type_id')?.value;
    const isPhoneType = [ContactTypes.PHONE, ContactTypes.MOBILE, ContactTypes.MOBILE_PHONE, ContactTypes.FAX].includes(
      typeId
    );

    if (isPhoneType) {
      const valueControl = contactGroup.get('value');
      if (valueControl && valueControl.value) {
        let val = valueControl.value as string;
        // Trim spaces and replace + with 00
        val = val.replace(/\s+/g, '').replace(/\+/g, '00');
        valueControl.setValue(val);
      }
    }
  }

  onPaste(index: number) {
    setTimeout(() => {
      this.formatPhoneNumber(index);
    });
  }

  onContactBlur(index: number) {
    this.formatPhoneNumber(index);
    this.checkContactExists(index);
  }

  /**
   * Създава много контакти от модел ...
   * @param contacts Взима броя на контактите и прави празени контакти
   * @param required Дали са задължителни полетата
   */
  public static createMany(contacts: Array<Contact>, required: boolean = false): FormArray {
    const formArray: FormArray = new FormArray([]);
    contacts.forEach((contact: Contact) => {
      formArray.push(ContactComponent.getEmptyContact(required));
    });
    return formArray;
  }

  ngOnInit() {
    // Смята колко трябва да е широка колоната на един контакт (single-contact) спрямо колко е широк parent елемента
    // Ако не е подадено нищо се приема, че parent-а е широк 12 колони и съответно колоната на един
    // контакт (single-contact) ще е широка 4 колони
    if (this.cols >= 12) {
      this.colsCalculated = 4;
    } else if (this.cols < 12 && this.cols > 6) {
      this.colsCalculated = 6;
    } else if (this.cols <= 6) {
      this.colsCalculated = 12;
    }

    setTimeout(() => {
      // @ts-ignore
      if (this.parentFormGroup.controls.contacts.length <= 0 && this.required) {
        this.addContact();
      }
    });
  }

  addContact() {
    log.debug('addContact');
    // @ts-ignore
    this.parentFormGroup.controls.contacts.push(ContactComponent.getEmptyContact(this.required));
  }

  removeContact(index: number) {
    this.parentFormGroup.get('contacts')['controls'].splice(index, 1);
    this.parentFormGroup.get('contacts').updateValueAndValidity();
  }

  checkContactExists(index: number) {
    const contactsArray = this.parentFormGroup.get('contacts') as FormArray;
    const contactGroup = contactsArray.at(index) as FormGroup;

    const typeId = contactGroup.get('type_id')?.value;
    const valueControl = contactGroup.get('value');
    const value = valueControl?.value;
    const contactId = contactGroup.get('id')?.value;
    const contactableId = this.parentFormGroup.get('id')?.value;

    if (!typeId || !value || valueControl?.errors?.invalidPhone) {
      return;
    }

    // mark as checked so save-time checks can skip already-checked contacts
    const dupCheckedControl = contactGroup.get('duplicateChecked');
    if (dupCheckedControl) dupCheckedControl.setValue(true, { emitEvent: false });

    this.http
      .get('v1/contacts/check-exists', {
        params: {
          type_id: typeId,
          value: value,
          ...(contactableId ? { contactable_id: contactableId } : {}),
          ...(contactId ? { contact_id: contactId } : {}),
        },
      })
      .subscribe({
        next: () => {
          // Ignore stale responses (value/type changed since request)
          if (valueControl?.value !== value || contactGroup.get('type_id')?.value !== typeId) {
            return;
          }
          // Clear non-blocking duplicate flag
          const dupControl = contactGroup.get('duplicate');
          if (dupControl) {
            dupControl.setValue(false, { emitEvent: false });
          }
          const dupMsgControl = contactGroup.get('duplicateMessage');
          if (dupMsgControl) {
            dupMsgControl.setValue(null, { emitEvent: false });
          }
        },
        error: (error) => {
          // Ignore stale responses (value/type changed since request)
          if (valueControl?.value !== value || contactGroup.get('type_id')?.value !== typeId) {
            return;
          }

          // Mark non-blocking duplicate flag so UI can show a warning, but do NOT set form errors
          if (error.status === 409) {
            const dupControl = contactGroup.get('duplicate');
            if (dupControl) {
              dupControl.setValue(true, { emitEvent: false });
            }
            const dupMsgControl = contactGroup.get('duplicateMessage');
            const serverMsg =
              (error && error.error && (error.error.message || error.error.msg)) || error.message || null;
            if (dupMsgControl) {
              dupMsgControl.setValue(serverMsg, { emitEvent: false });
            }
          }
        },
      });
  }

  /**
   * Check all contacts for duplicates. Sets each contact's `duplicate` flag.
   * Returns an Observable that emits `true` if any duplicate was found.
   */
  public checkAllContactsExists(): Observable<boolean> {
    const contactsArray = this.parentFormGroup.get('contacts') as FormArray;
    if (!contactsArray || contactsArray.length <= 0) {
      return of(false);
    }

    const checks: Observable<boolean>[] = [];

    for (let i = 0; i < contactsArray.length; i++) {
      const contactGroup = contactsArray.at(i) as FormGroup;
      const typeId = contactGroup.get('type_id')?.value;
      const valueControl = contactGroup.get('value');
      const value = valueControl?.value;
      const contactId = contactGroup.get('id')?.value;
      const contactableId = this.parentFormGroup.get('id')?.value;

      const dupCheckedControl = contactGroup.get('duplicateChecked');
      const dupControl = contactGroup.get('duplicate');
      const dupMsgControl = contactGroup.get('duplicateMessage');

      if (dupCheckedControl?.value) {
        // Already checked earlier — reuse its duplicate state
        checks.push(of(!!dupControl?.value));
        continue;
      }

      if (!typeId || !value || valueControl?.errors?.invalidPhone) {
        // ensure duplicate flag cleared and mark as checked
        if (dupControl) dupControl.setValue(false, { emitEvent: false });
        if (dupMsgControl) dupMsgControl.setValue(null, { emitEvent: false });
        if (dupCheckedControl) dupCheckedControl.setValue(true, { emitEvent: false });
        checks.push(of(false));
        continue;
      }

      const obs = this.http
        .get('v1/contacts/check-exists', {
          params: {
            type_id: typeId,
            value: value,
            ...(contactableId ? { contactable_id: contactableId } : {}),
            ...(contactId ? { contact_id: contactId } : {}),
          },
        })
        .pipe(
          map(() => {
            // no duplicate
            if (dupControl) dupControl.setValue(false, { emitEvent: false });
            if (dupMsgControl) dupMsgControl.setValue(null, { emitEvent: false });
            if (dupCheckedControl) dupCheckedControl.setValue(true, { emitEvent: false });
            return false;
          }),
          catchError((err) => {
            const dup = err && err.status === 409;
            if (dupControl) dupControl.setValue(!!dup, { emitEvent: false });
            const serverMsg = (err && err.error && (err.error.message || err.error.msg)) || err.message || null;
            if (dupMsgControl) dupMsgControl.setValue(serverMsg, { emitEvent: false });
            if (dupCheckedControl) dupCheckedControl.setValue(true, { emitEvent: false });
            return of(!!dup);
          })
        );

      checks.push(obs);
    }

    return forkJoin(checks).pipe(map((results) => results.some((r) => !!r)));
  }

  getDuplicateContactMessage(typeId: number): string {
    switch (typeId) {
      case ContactTypes.EMAIL:
        return 'CONTACT_COMPONENT.DUPLICATE_EMAIL';

      case ContactTypes.PHONE:
      case ContactTypes.MOBILE:
      case ContactTypes.MOBILE_PHONE:
        return 'CONTACT_COMPONENT.DUPLICATE_PHONE';

      case ContactTypes.FAX:
        return 'CONTACT_COMPONENT.DUPLICATE_FAX';

      case ContactTypes.EXTENSION:
        return 'CONTACT_COMPONENT.DUPLICATE_EXTENSION';

      default:
        return 'CONTACT_COMPONENT.DUPLICATE_CONTACT';
    }
  }
}
